import {
  $isReferenceCitationNode,
  ReferenceCitationNode,
} from '../../../nodes/ReferenceCitationNode';
import {
  $isReferenceCitationSeparatorNode,
  ReferenceCitationSeparatorNode,
} from '../../../nodes/ReferenceCitationSeparatorNode';

type ReferencesNode = ReferenceCitationNode | ReferenceCitationSeparatorNode;

function getNumber(
  node: ReferenceCitationNode | null,
  numbersMap: Record<string, number>,
): number {
  if (!node) {
    return -1;
  }

  return numbersMap[node.getReferenceId()] ?? -1;
}

function getNextReference(node: ReferenceCitationNode): ReferenceCitationNode | null {
  let next = node.getNextSibling();
  if ($isReferenceCitationNode(next)) {
    return next;
  }
  if (!$isReferenceCitationSeparatorNode(next)) {
    return null;
  }
  next = next.getNextSibling();

  return $isReferenceCitationNode(next) ? next : null;
}

function getPreviousReference(node: ReferenceCitationNode): ReferenceCitationNode | null {
  let prev = node.getPreviousSibling();
  if ($isReferenceCitationNode(prev)) {
    return prev;
  }
  if (!$isReferenceCitationSeparatorNode(prev)) {
    return null;
  }
  prev = prev.getPreviousSibling();

  return $isReferenceCitationNode(prev) ? prev : null;
}

function getNextSeparator(node: ReferenceCitationNode): ReferenceCitationSeparatorNode | null {
  const next = node.getNextSibling();

  return $isReferenceCitationSeparatorNode(next) ? next : null;
}

export function $setNodeText(
  node: ReferenceCitationNode,
  numbersMap: Record<string, number>,
): ReferencesNode[] {
  node.setNumber(getNumber(node, numbersMap));
  const separator = getNextSeparator(node);
  if (!separator) {
    return [node];
  }

  return [node, separator.convertToComma()];

}

function setEditorNodeNumber(
  node: ReferenceCitationNode,
  value: number,
): void {
  node.setNumber(value);
}

export function $setNodeTextWithNumbersRange(
  node: ReferenceCitationNode,
  numbersMap: Record<string, number>,
  isPreview = false,
): ReferencesNode[] {

  const nextReference = getNextReference(node);
  const separator = nextReference ? getNextSeparator(node) : null;
  const mutadedNode = separator ? [node, separator] : [node];

  if (isPreview) {
    node.setNumber(undefined);
    separator?.convertToComma();

    return mutadedNode;
  }

  const current = getNumber(node, numbersMap);

  // current citation is the last number in the list
  if (!nextReference) {

    setEditorNodeNumber(node, current);

    return [node];
  }

  const prev = getNumber(getPreviousReference(node), numbersMap);
  const next = getNumber(nextReference, numbersMap);
  const afterNext = getNumber(getNextReference(nextReference), numbersMap);

  const returnWithComma = (): ReferencesNode[] => {
    setEditorNodeNumber(node, current);
    separator?.convertToComma();

    return mutadedNode;
  };

  const returnWithDash = (): ReferencesNode[] => {
    setEditorNodeNumber(node, current);
    separator?.convertToDash();

    return mutadedNode;
  };

  const returnHidden = (): ReferencesNode[] => {
    node.setHidden();
    separator?.convertToHidden();

    return mutadedNode;
  };

  // current citation number or next one is missing, the actual position in the list doesn't matter
  if (current <= 0 || next <= 0) {

    return returnWithComma();
  }

  // current citatation is in the middle of the list
  if (prev === current - 1) {
    if (next === current + 1) {
      return returnHidden();
    }

    return returnWithComma();
  }

  // check if the current ciration is a begging of the consequent list
  if (next === current + 1 && afterNext === current + 2) {

    return returnWithDash();
  }

  return returnWithComma();
}
